import { Select } from "@mantine/core";
import { DataTableColumn } from "mantine-datatable";
import React, { useCallback, useMemo } from "react";

import { DatabaseMetadataPopover } from "../../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import { TableData, TableRow } from "../../../../components/Table/types";
import {
  LimitRequestType,
  LimitTopProductCategory,
  RolePermission,
  UpdateCuAssignedInput,
  UpdateLimitRequestInlineInput,
} from "../../../../graphql/generated.ts";
import { useCurrentUserContext } from "../../../../providers/CurrentUserProvider.tsx";
import {
  LimitRequest,
  toHumanReadableLimitRequestStatus,
  topProductCategoryOptions,
} from "../../../../types/limitRequest/limitRequest.ts";
import { UserOption } from "../../../../types/user.ts";
import { toLocalDate } from "../../../../utils/date";
import { toCurrencyPrice, toLocalNumber } from "../../../../utils/number";
import { toHumanReadable } from "../../../../utils/string";
import {
  getUserDisplayName,
  getUserOptions,
  hasPermission,
} from "../../../../utils/user.ts";

interface Props {
  assignees: UserOption[] | null | undefined;
  data: LimitRequest[] | null | undefined;
  onEdit: (id: number, value: UpdateLimitRequestInlineInput) => void;
  onEditCUAssigned: (id: number, input: UpdateCuAssignedInput) => void;
}

export const useNewLimitRequestedTableData = ({
  data,
  assignees,
  onEdit,
  onEditCUAssigned,
}: Props): TableData => {
  const { user } = useCurrentUserContext();

  const canEditLimitRequest = hasPermission(
    user,
    RolePermission.CanEditLimitRequest
  );

  const canAssignCU = hasPermission(
    user,
    RolePermission.CanAssignLimitRequestCu
  );

  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "id",
      hidden: true,
    },
    {
      accessor: "createdAt",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "createdAt",
          }}
        >
          Limit Request Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "limitName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "limitName",
          }}
        >
          Limit Name
        </DatabaseMetadataPopover>
      ),
      width: 300,
      sortable: true,
    },
    {
      accessor: "companyName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Customer",
            fieldName: "name",
          }}
        >
          Company Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 300,
    },
    {
      accessor: "salesName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Customer",
            fieldName: "salesName",
          }}
        >
          Sales Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "salesTeamName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Customer",
            fieldName: "salesTeamName",
          }}
        >
          Sales Team
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "requestStatus",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "requestStatus",
          }}
        >
          Request Status
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "limitRequestAmount",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "limitRequestAmount",
          }}
        >
          Limit Request
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "tenorRequest",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "tenorRequest",
          }}
        >
          Tenor Request
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "limitType",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "limitRequestType",
          }}
        >
          Limit Type
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "topProductCategory",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "topProductCategory",
          }}
        >
          TOP Product Category
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "cuAssignedId",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "cuAssignedId",
          }}
        >
          CU Assigned
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 250,
    },
  ];

  const handleOnClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    //  stop event propagation (won't trigger parent's onClick)
    event.stopPropagation();
  }, []);

  const handleEditCUAssigned = useCallback(
    (id: number, cuAssignedId: number) => {
      onEditCUAssigned(id, { cuAssignedId, cuAssignedDate: new Date() });
    },
    [onEditCUAssigned]
  );

  const renderTopUpCategory = useCallback(
    (item: LimitRequest) => {
      if (item.limitRequestType === LimitRequestType.NewProjectLimit) {
        return toHumanReadable(LimitTopProductCategory.ProjectLimit);
      }
      if (item.limitRequestType === LimitRequestType.TopUpLimit) {
        return toHumanReadable(LimitTopProductCategory.TopUpLimit);
      }
      return (
        <Select
          data={topProductCategoryOptions}
          value={item.topProductCategory}
          withinPortal
          zIndex={100}
          size="s"
          disabled={!canEditLimitRequest}
          onChange={(value: LimitTopProductCategory) =>
            onEdit(item.id, { topProductCategory: value })
          }
          onClick={(event) => handleOnClick(event)}
        />
      );
    },
    [canEditLimitRequest, handleOnClick, onEdit]
  );

  const rows = useMemo(() => {
    return data?.map((item) => {
      return {
        id: item.id,
        createdAt: toLocalDate(item.createdAt),
        limitName: item.limitName,
        companyName: item.company.name,
        salesName: item.company.salesName,
        salesTeamName: item.company.salesTeamName,
        limitRequestAmount: toCurrencyPrice(item.limitRequestAmount),
        tenorRequest: toLocalNumber(item.tenorRequest),
        limitType:
          item?.limitRequestType && toHumanReadable(item.limitRequestType),
        topProductCategory: renderTopUpCategory(item),
        cuAssignedId: canAssignCU ? (
          <Select
            data={getUserOptions(assignees)}
            value={item.cuAssigned?.id.toString()}
            withinPortal
            zIndex={100}
            size="s"
            onChange={(value) => handleEditCUAssigned(item.id, Number(value))}
            onClick={(event) => handleOnClick(event)}
          />
        ) : (
          getUserDisplayName(item.cuAssigned)
        ),
        requestStatus: toHumanReadableLimitRequestStatus(item.requestStatus),
      };
    });
  }, [
    data,
    renderTopUpCategory,
    canAssignCU,
    assignees,
    handleEditCUAssigned,
    handleOnClick,
  ]);

  return [columns, rows];
};
